import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';
import { formatCurrency } from 'utils/NumberHelper';
import { expiry, dateFormat } from 'utils/DateHelper';

import { getEstimatedEarRate } from 'enums/banks';

import CreditLineActivity from './CreditLineActivityModel';
import CreditLineFacility from './CreditLineFacilityModel';

const CreditLineRecord = Record({
  id: 0,
  facility: null,
  activity: null,
});

export default class CreditLine extends CreditLineRecord {
  static deserialize(jsonObject = {}) {
    const mapProperties = {
      facility: CreditLineFacility.deserialize,
      activity: CreditLineActivity.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new CreditLine(hydrated);
  }

  get productTypeLabel() {
    return this.facility.productTypeLabel || '';
  }

  get repaid() {
    return this.facility.amount - Math.abs(this.activity.balance);
  }

  get percentRepaid() {
    if (!this.facility.amount) {
      return 0;
    }

    return Math.min(Math.floor((this.repaid / this.facility.amount) * 100), 100);
  }

  get isSuccessful() {
    return this.percentRepaid > 80;
  }

  get bankBalanceLabel() {
    return this.activity && this.activity.live ? 'Live Balance' : 'Ledger Balance';
  }

  get balance() {
    return formatCurrency(Math.round(this.activity.balance));
  }

  get averageBalance() {
    return this.activity.live ? formatCurrency(this.activity.live.metrics.averageYearBalance) : '-';
  }

  get equivalentAnnualRate() {
    return getEstimatedEarRate(this.facility.bankNumber);
  }

  get estimatedCost() {
    return this.activity.live
      ? formatCurrency(this.activity.live.metrics.overdraft.estimatedCost)
      : '-';
  }

  get expiry() {
    const { endDate } = this.facility;
    return endDate ? expiry(endDate) : '-';
  }

  get productStartDate() {
    const { startDate } = this.facility;
    return startDate ? dateFormat(startDate, 'do MMMM yyyy') : '-';
  }

  get productEndDate() {
    const { endDate } = this.facility;
    return endDate ? dateFormat(endDate, 'do MMMM yyyy') : '-';
  }
}
